import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  List,
  ListItem,
  Divider,
  Tooltip,
  IconButton,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Chip,
  Typography,
  Stack,
} from '@mui/material';
import {
  History as HistoryIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Notes as TextIcon,
  Today as CalendarIcon,
  Restore as RestoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { 
  getParaphraseHistory, 
  deleteParaphrase, 
  updateParaphrase 
} from "../../../services/paraphraserHistoryService";

const ParaphraseHistory = ({ open, onClose, onRestoreParaphrase, userId, machineId }) => {
  const [paraphrases, setParaphrases] = useState([]);
  const [filteredParaphrases, setFilteredParaphrases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedParaphrase, setSelectedParaphrase] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editText, setEditText] = useState("");
  const [editId, setEditId] = useState(null);
  const [filterTone, setFilterTone] = useState("all");

  const itemsPerPage = 10;

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open, userId, machineId]);

  useEffect(() => {
    filterParaphrases();
  }, [searchTerm, paraphrases, filterTone]);

  const filterParaphrases = () => {
    let filtered = [...paraphrases];
    
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.originalText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.outputText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.toneType?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterTone !== "all") {
      filtered = filtered.filter((item) => item.toneType === filterTone);
    }
    
    setFilteredParaphrases(filtered);
    setPage(1);
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await getParaphraseHistory(userId, machineId);
      if (response.success) {
        setParaphrases(response.data);
        setFilteredParaphrases(response.data);
      }
    } catch (error) {
      showSnackbar("Failed to load paraphrase history", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (paraphrase) => {
    onRestoreParaphrase(paraphrase.outputText);
    showSnackbar("Paraphrased content restored successfully", "success");
    onClose();
  };

  const handleDelete = async () => {
    if (!selectedParaphrase) return;
    
    try {
      await deleteParaphrase(selectedParaphrase._id);
      showSnackbar("Paraphrase deleted successfully", "success");
      fetchHistory();
      setDeleteDialogOpen(false);
      setSelectedParaphrase(null);
    } catch (error) {
      showSnackbar("Failed to delete paraphrase", "error");
    }
  };

  const handleEdit = (paraphrase) => {
    setEditId(paraphrase._id);
    setEditText(paraphrase.outputText);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editId || !editText.trim()) return;
    
    try {
      await updateParaphrase(editId, editText);
      showSnackbar("Paraphrase updated successfully", "success");
      fetchHistory();
      setEditDialogOpen(false);
      setEditText("");
      setEditId(null);
    } catch (error) {
      showSnackbar("Failed to update paraphrase", "error");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getToneIcon = (toneType) => {
    const icons = {
      standard: "📝",
      formal: "🎩",
      casual: "😊",
      positive: "✨",
      confident: "💪",
      friendly: "🤝",
      simple: "🔹",
      shorten: "🔽",
      expand: "🔼",
      custom: "🎨",
    };
    return icons[toneType] || "📝";
  };

  const getToneLabel = (toneType) => {
    const labels = {
      standard: "Standard",
      formal: "Formal",
      casual: "Casual",
      positive: "Positive",
      confident: "Confident",
      friendly: "Friendly",
      simple: "Simple",
      shorten: "Shortened",
      expand: "Expanded",
      custom: "Custom Tone",
    };
    return labels[toneType] || toneType;
  };

  const totalPages = Math.ceil(filteredParaphrases.length / itemsPerPage);
  const paginatedParaphrases = filteredParaphrases.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 550, md: 650 },
            bgcolor: "background.paper",
            borderRadius: { sm: "16px 0 0 16px" },
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#10b981",
              color: "white",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HistoryIcon />
              <Typography variant="h6">Paraphrase History</Typography>
              <Chip
                label={`${paraphrases.length} items`}
                size="small"
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
              />
            </Box>
            <IconButton onClick={onClose} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search and Filter Bar */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by content or tone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <ToggleButtonGroup
              value={filterTone}
              exclusive
              onChange={(e, value) => value && setFilterTone(value)}
              size="small"
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="standard">Standard</ToggleButton>
              <ToggleButton value="formal">Formal</ToggleButton>
              <ToggleButton value="casual">Casual</ToggleButton>
              <ToggleButton value="friendly">Friendly</ToggleButton>
              <ToggleButton value="shorten">Shorten</ToggleButton>
              <ToggleButton value="expand">Expand</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* List of Paraphrases */}
          <Box sx={{ flex: 1, overflow: "auto", px: 2 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : paginatedParaphrases.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <HistoryIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography color="text.secondary">No saved paraphrases yet</Typography>
                <Typography variant="body2" color="text.secondary">
                  Your paraphrased content will appear here
                </Typography>
              </Box>
            ) : (
              <List>
                {paginatedParaphrases.map((item, index) => (
                  <React.Fragment key={item._id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        py: 2,
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Box sx={{ width: "100%", mb: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              {/* Tone Badge */}
                              <Chip
                                icon={<span>{getToneIcon(item.toneType)}</span>}
                                label={getToneLabel(item.toneType)}
                                size="small"
                                sx={{
                                  bgcolor: "#10b981",
                                  color: "white",
                                  fontWeight: 600,
                                }}
                              />
                              
                              {/* Word Count */}
                              <Chip
                                icon={<TextIcon />}
                                label={`${item.wordCount || item.outputText?.split(/\s+/).length || 0} words`}
                                size="small"
                                variant="outlined"
                              />
                              
                              {/* Date */}
                              <Chip
                                icon={<CalendarIcon />}
                                label={formatDate(item.createdAt)}
                                size="small"
                                variant="outlined"
                              />
                            </Stack>
                          </Box>
                          
                          {/* Action Buttons */}
                          <Box>
                            <Tooltip title="Restore">
                              <IconButton
                                size="small"
                                onClick={() => handleRestore(item)}
                                sx={{ color: "success.main" }}
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(item)}
                                sx={{ color: "info.main" }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedParaphrase(item);
                                  setDeleteDialogOpen(true);
                                }}
                                sx={{ color: "error.main" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* Original Text Preview */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Original:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            mb: 1,
                            pl: 1,
                            borderLeft: 2,
                            borderColor: "primary.light",
                          }}
                        >
                          {item.originalText?.substring(0, 150)}
                          {item.originalText?.length > 150 && "..."}
                        </Typography>

                        {/* Paraphrased Text Preview */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Paraphrased:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            pl: 1,
                            borderLeft: 2,
                            borderColor: "#10b981",
                            fontWeight: 500,
                          }}
                        >
                          {item.outputText?.substring(0, 200)}
                          {item.outputText?.length > 200 && "..."}
                        </Typography>
                        
                        {/* Metadata Stats */}
                        {item.metadata?.wordCountDifference !== 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {item.metadata.wordCountDifference > 0 
                                ? `📈 Expanded by ${item.metadata.wordCountDifference} words`
                                : item.metadata.wordCountDifference < 0
                                ? `✂️ Reduced by ${Math.abs(item.metadata.wordCountDifference)} words`
                                : null
                              }
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {totalPages > 1 && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                size="small"
                sx={{ display: "flex", justifyContent: "center" }}
              />
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Paraphrased Text</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Paraphrased Text"
            type="text"
            fullWidth
            multiline
            rows={10}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Paraphrase</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this paraphrased content?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ParaphraseHistory;